/**
 * Le code parent du mode enfant (HARMONIA-87).
 *
 * Quatre chiffres choisis par le parent la première fois qu'il passe un
 * appareil en mode enfant. Il ne sert qu'à une chose : en sortir. Il vit sur
 * l'appareil seulement, jamais en base : c'est un verrou de tablette familiale,
 * pas un identifiant de compte, et un parent qui l'oublie se déconnecte puis se
 * reconnecte, ce qu'un enfant ne peut pas faire.
 *
 * Stocké haché avec un sel. À quatre chiffres, le hachage ne résisterait pas à
 * qui lirait le stockage de l'app ; il évite seulement de garder le code en
 * clair. La vraie protection est l'attente imposée après cinq erreurs.
 *
 * Cette attente DOUBLE à chaque nouvelle série d'erreurs (30 s, 1 min, 2 min…
 * jusqu'à une heure) et ne revient à 30 s qu'après un bon code. À attente fixe,
 * 10 000 codes tombaient en moins d'un jour à un enfant patient ; sur son
 * propre téléphone appairé (#402), il a tout le temps.
 */
import { Preferences } from '@capacitor/preferences'
import { sha256Hex } from '@/lib/sha256'

export const PARENT_CODE_LENGTH = 4
export const MAX_FAILURES = 5
export const LOCKOUT_MS = 30_000
export const MAX_LOCKOUT_MS = 60 * 60_000

const CODE_KEY = 'harmonia_parent_code'
const ATTEMPTS_KEY = 'harmonia_parent_code_attempts'

interface StoredCode { salt: string; hash: string }
interface Attempts {
  failures: number
  lockedUntil: number
  /** Séries de cinq erreurs déjà sanctionnées depuis le dernier bon code. */
  lockouts?: number
}

/** L'attente imposée à la série n (0 pour la première). */
export function lockoutDuration(lockouts: number): number {
  return Math.min(LOCKOUT_MS * 2 ** lockouts, MAX_LOCKOUT_MS)
}

// Discriminant en chaîne et non en booléen : le projet n'est pas en `strict`,
// et sans strictNullChecks un `ok: true | false` ne restreint pas l'union.
export type VerifyResult =
  | { status: 'ok' }
  | { status: 'wrong'; remaining: number }
  | { status: 'locked'; retryInMs: number }

export function isValidParentCode(code: string): boolean {
  return new RegExp(`^\\d{${PARENT_CODE_LENGTH}}$`).test(code)
}

async function readJson<T>(key: string): Promise<T | null> {
  try {
    const { value } = await Preferences.get({ key })
    return value ? (JSON.parse(value) as T) : null
  } catch {
    return null
  }
}

async function writeJson(key: string, value: unknown): Promise<void> {
  await Preferences.set({ key, value: JSON.stringify(value) })
}

function randomSalt(): string {
  const bytes = new Uint8Array(16)
  crypto.getRandomValues(bytes)
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}

async function hashCode(code: string, salt: string): Promise<string> {
  return sha256Hex(`${salt}:${code}`)
}

async function readAttempts(): Promise<Attempts> {
  return (await readJson<Attempts>(ATTEMPTS_KEY)) ?? { failures: 0, lockedUntil: 0 }
}

export async function hasParentCode(): Promise<boolean> {
  const stored = await readJson<StoredCode>(CODE_KEY)
  return !!stored?.salt && !!stored?.hash
}

export async function setParentCode(code: string): Promise<void> {
  if (!isValidParentCode(code)) throw new Error('INVALID_PARENT_CODE')
  const salt = randomSalt()
  await writeJson(CODE_KEY, { salt, hash: await hashCode(code, salt) } satisfies StoredCode)
  await Preferences.remove({ key: ATTEMPTS_KEY })
}

/** Millisecondes d'attente restantes, 0 si l'on peut saisir. */
export async function parentCodeLockoutRemaining(now = Date.now()): Promise<number> {
  const { lockedUntil } = await readAttempts()
  return Math.max(0, lockedUntil - now)
}

export async function verifyParentCode(code: string, now = Date.now()): Promise<VerifyResult> {
  const attempts = await readAttempts()
  if (attempts.lockedUntil > now) {
    return { status: 'locked', retryInMs: attempts.lockedUntil - now }
  }

  const stored = await readJson<StoredCode>(CODE_KEY)
  // Sans code enregistré, rien ne peut être vérifié : on refuse plutôt que
  // d'ouvrir. Le seul chemin restant est « Code oublié ? ».
  const ok = !!stored && isValidParentCode(code) && (await hashCode(code, stored.salt)) === stored.hash

  if (ok) {
    await Preferences.remove({ key: ATTEMPTS_KEY })
    return { status: 'ok' }
  }

  const failures = attempts.failures + 1
  const lockouts = attempts.lockouts ?? 0
  if (failures >= MAX_FAILURES) {
    const wait = lockoutDuration(lockouts)
    await writeJson(ATTEMPTS_KEY, { failures: 0, lockedUntil: now + wait, lockouts: lockouts + 1 } satisfies Attempts)
    return { status: 'locked', retryInMs: wait }
  }
  await writeJson(ATTEMPTS_KEY, { failures, lockedUntil: 0, lockouts } satisfies Attempts)
  return { status: 'wrong', remaining: MAX_FAILURES - failures }
}

export async function clearParentCode(): Promise<void> {
  await Preferences.remove({ key: CODE_KEY })
  await Preferences.remove({ key: ATTEMPTS_KEY })
}
